import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useChatStreamValue } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { useUIStore } from "src/views/assistant/stores/UIStore";
import { ChatMessageWithParts, TArtifact } from "src/views/assistant/types";
import UnifiedSidebar from "src/reusable/Sidebar/UnifiedSidebar";
import { useProposalGenerationSidebar } from "../../hooks/useProposalGenerationSidebar";
import { useProposalGenerationStore } from "../../store/ProposalGenerationStore";
import {
  useGetProposalGenerationStages,
  useGetProposalGenerationDialogueData,
  useGetProposalGenerationPlans,
  useClearProposalGenerationStage,
  useRemoveStageFromRun,
  useAddStageToRun,
} from "../../services/ProposalGenerationServices";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import SidebarToggleButton from "src/reusable/Sidebar/SidebarToggleButton";
import { NucToast } from "src/reusable/components/Toast";
import { navigateToStage } from "../../utils/navigationUtils";
import { extractPlanIdsFromArtifacts } from "../../utils/planIdExtraction";

const EMPTY_ARTIFACTS: Record<string, TArtifact> = {};

interface ProposalGenerationSidebarProps {
  responseSources: any[];
  lastAssistantMessage?: ChatMessageWithParts | null;
  onIdentifyStages?: () => void;
  isIdentifyingStages?: boolean;
}

/**
 * ProposalGenerationSidebar - Self-contained sidebar for Proposal Generation
 */
export const ProposalGenerationSidebar = memo(
  ({
    responseSources,
    onIdentifyStages,
    isIdentifyingStages,
  }: ProposalGenerationSidebarProps) => {
    const { isMobile } = useResponsive();
    const history = useHistory();

    // Get store state
    const {
      runId: proposalGenerationRunId,
      dialogueId,
      inputFiles,
      setStages,
      setStageReportPrefix,
      setStageReportSuffixLabel,
    } = useProposalGenerationStore();
    const { dropdownDatasets } = useChatStore();
    const { rightSidebar, setRightSidebar } = useUIStore();
    const showSideBar = rightSidebar.isOpen;

    // Fetch stages data and sync to store
    const { data: stages } = useGetProposalGenerationStages(
      proposalGenerationRunId,
    );

    useEffect(() => {
      setStages(stages || []);
      // All stages in a run share the run-level prefix + suffix; snapshot them
      // for the header/report-panel display (which work off the current dialogue).
      setStageReportPrefix(stages?.[0]?.stage_report_prefix ?? null);
      setStageReportSuffixLabel(stages?.[0]?.stage_report_suffix_label ?? null);
    }, [stages, setStages, setStageReportPrefix, setStageReportSuffixLabel]);

    // Fetch dialogue data for template_id and report_sdr_id
    const { data: proposalGenDialogueData, refetch: refetchProposalData } =
      useGetProposalGenerationDialogueData(dialogueId);

    // Derive report SDR ID from proposal data (already fetched via React Query)
    const reportSdrId = proposalGenDialogueData?.report_sdr_id ?? null;

    // Local state for view mode
    const [viewMode, setViewMode] = useState<"dataChunk" | "cardList">(
      "dataChunk",
    );

    // Read artifacts from the host's stream (provided by
    // ProposalGenerationChat).
    const artifacts = useChatStreamValue((s) => s.artifacts, EMPTY_ARTIFACTS);

    // Compute stable artifacts length for dependency tracking
    const artifactsLength = Object.keys(artifacts).length;

    // Watch for "Generate Report", "Modify Document", or "Share File"
    // tool completion to refetch proposal data.
    // This detects when a report is first generated (or regenerated) so the
    // report tab can appear in the sidebar.
    const completedReportArtifactCount = Object.values(artifacts).filter(
      (artifact: TArtifact) =>
        (artifact.toolName?.toLowerCase().replace(/ /g, "_") ===
          "generate_report" ||
          artifact.toolName?.toLowerCase().replace(/ /g, "_") ===
            "modify_document" ||
          artifact.toolName?.toLowerCase().replace(/ /g, "_") ===
            "share_file") &&
        artifact.content?.text &&
        !artifact.isLoading,
    ).length;
    const hasGenerateReportBeenCalled = completedReportArtifactCount > 0;
    const [processedReportCount, setProcessedReportCount] = useState(0);

    // Reset processed count when dialogue changes
    useEffect(() => {
      setProcessedReportCount(0);
    }, [dialogueId]);

    useEffect(() => {
      if (
        completedReportArtifactCount > 0 &&
        completedReportArtifactCount > processedReportCount
      ) {
        setProcessedReportCount(completedReportArtifactCount);
        refetchProposalData();
        // Auto-open sidebar to report tab when Generate Report tool completes
        setRightSidebar({ isOpen: true, selectedItem: "report" });
      }
    }, [
      completedReportArtifactCount,
      processedReportCount,
      refetchProposalData,
      setRightSidebar,
    ]);

    // Extract plan_ids from create/checkout/update/complete plan tool responses.
    const planIds = useMemo(
      () => extractPlanIdsFromArtifacts(artifacts),
      [artifacts],
    );

    // Fetch the actual Plan rows from the DB.
    const { data: plans = [], refetch: refetchPlans } =
      useGetProposalGenerationPlans(planIds);

    // Watch for create_plan / checkout_plan / update_plan / complete_plan
    // tool completion to drive the refetch + auto-open. Mirrors the report
    // watcher above.
    const completedPlanArtifactCount = Object.values(artifacts).filter(
      (artifact: TArtifact) => {
        const tool = artifact.toolName?.toLowerCase().replace(/ /g, "_");
        return (
          (tool === "create_plan" ||
            tool === "checkout_plan" ||
            tool === "update_plan" ||
            tool === "complete_plan") &&
          artifact.content?.text &&
          !artifact.isLoading
        );
      },
    ).length;
    const [processedPlanCount, setProcessedPlanCount] = useState(0);

    // Reset on dialogue change
    useEffect(() => {
      setProcessedPlanCount(0);
    }, [dialogueId]);

    useEffect(() => {
      if (
        completedPlanArtifactCount > 0 &&
        completedPlanArtifactCount > processedPlanCount
      ) {
        const isFirstPlan = processedPlanCount === 0;
        setProcessedPlanCount(completedPlanArtifactCount);
        refetchPlans();
        // Auto-open the Plan tab only on the first plan creation.
        if (isFirstPlan) {
          setRightSidebar({ isOpen: true, selectedItem: "plan" });
        }
      }
    }, [
      completedPlanArtifactCount,
      processedPlanCount,
      refetchPlans,
      setRightSidebar,
    ]);

    // Initialize sidebar state when navigating to new dialogue.
    // Start minimized to reduce clutter — the sidebar will auto-open when
    // a report is generated or the user manually opens a tab.
    useEffect(() => {
      setRightSidebar({ isOpen: false, selectedItem: null });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogueId, setRightSidebar]);

    // Memoize total sidebar items for mobile badge
    const totalSidebarItems = useMemo(() => {
      return (
        responseSources.length +
        artifactsLength +
        (stages?.length || 0) +
        inputFiles.length +
        (dropdownDatasets?.length || 0) +
        (proposalGenDialogueData?.template_id ? 1 : 0) +
        (reportSdrId ? 1 : 0) +
        plans.length
      );
    }, [
      responseSources.length,
      artifactsLength,
      stages?.length,
      inputFiles.length,
      dropdownDatasets?.length,
      proposalGenDialogueData?.template_id,
      reportSdrId,
      plans.length,
    ]);

    // Mutation hooks for stage management
    const clearStageMutation = useClearProposalGenerationStage();
    const removeStageMutation = useRemoveStageFromRun();
    const addStageMutation = useAddStageToRun();

    // Handler for navigating to stages
    const handleStageClick = useCallback(
      (targetDialogueId: number, stageNumber?: number) => {
        navigateToStage(
          history,
          proposalGenerationRunId,
          stages,
          targetDialogueId,
          stageNumber,
          "[ProposalGenerationSidebar]",
        );
      },
      [history, proposalGenerationRunId, stages],
    );

    // Handler for clearing a stage
    const handleClearStage = useCallback(
      (targetDialogueId: number) => {
        if (!proposalGenerationRunId) return;
        const stage = stages?.find((s) => s.dialogue_id === targetDialogueId);
        clearStageMutation.mutate(
          { dialogueId: targetDialogueId, runId: proposalGenerationRunId },
          {
            onSuccess: () => {
              // Navigate to splash page for the cleared stage's new dialogue
              if (stage?.sequence_number) {
                history.push(
                  `/proposal-generation?runId=${proposalGenerationRunId}&stageNumber=${stage.sequence_number}`,
                );
              }
            },
            onError: () => {
              NucToast({ message: "Failed to reset stage", type: "error" });
            },
          },
        );
      },
      [
        proposalGenerationRunId,
        dialogueId,
        stages,
        clearStageMutation,
        history,
      ],
    );

    // Handler for removing a stage
    const handleRemoveStage = useCallback(
      (targetDialogueId: number) => {
        if (!proposalGenerationRunId) return;
        removeStageMutation.mutate(
          { runId: proposalGenerationRunId, dialogueId: targetDialogueId },
          {
            onSuccess: () => {
              // If removing the current dialogue, navigate to review report chat
              if (targetDialogueId === dialogueId) {
                const reviewStage = stages?.find(
                  (s) => s.sequence_number === 1,
                );
                if (reviewStage) {
                  history.push(
                    `/proposal-generation/chat/${proposalGenerationRunId}/${reviewStage.dialogue_id}`,
                  );
                }
              }
            },
            onError: () => {
              NucToast({ message: "Failed to remove stage", type: "error" });
            },
          },
        );
      },
      [
        proposalGenerationRunId,
        dialogueId,
        stages,
        removeStageMutation,
        history,
      ],
    );

    // Handler for adding a stage
    const handleAddStage = useCallback(
      (reportType: string) => {
        if (!proposalGenerationRunId) return;
        addStageMutation.mutate(
          {
            runId: proposalGenerationRunId,
            payload: { report_type: reportType },
          },
          {
            onError: () => {
              NucToast({ message: "Failed to add stage", type: "error" });
            },
          },
        );
      },
      [proposalGenerationRunId, addStageMutation],
    );

    const isMutating =
      clearStageMutation.isLoading ||
      removeStageMutation.isLoading ||
      addStageMutation.isLoading;

    // Memoize context to prevent unnecessary re-renders of UnifiedSidebar (which is wrapped in memo)
    const sidebarContext = useMemo(
      () => ({
        responseSources,
        artifacts,
        viewMode,
        setViewMode,
        stages: stages || [],
        currentDialogueId: dialogueId || 0,
        onStageClick: handleStageClick,
        onClearStage: handleClearStage,
        onRemoveStage: handleRemoveStage,
        onAddStage: handleAddStage,
        isMutating,
        inputDocuments: inputFiles,
        referenceDatasets: dropdownDatasets || [],
        templateId: proposalGenDialogueData?.template_id ?? null,
        reportSdrId,
        hasGenerateReportBeenCalled,
        dialogueId: dialogueId || undefined,
        onIdentifyStages,
        isIdentifyingStages,
        plans,
      }),
      [
        responseSources,
        artifacts,
        viewMode,
        stages,
        dialogueId,
        handleStageClick,
        handleClearStage,
        handleRemoveStage,
        handleAddStage,
        isMutating,
        inputFiles,
        dropdownDatasets,
        proposalGenDialogueData?.template_id,
        reportSdrId,
        hasGenerateReportBeenCalled,
        onIdentifyStages,
        isIdentifyingStages,
        plans,
      ],
    );

    // Get sidebar configuration
    const sidebarConfig = useProposalGenerationSidebar(sidebarContext);

    // Mobile badge content
    const handleMobileToggle = useCallback(() => {
      setRightSidebar({
        isOpen: !showSideBar,
        ...(!showSideBar &&
          rightSidebar.selectedItem === null && { selectedItem: "sources" }),
      });
    }, [showSideBar, rightSidebar.selectedItem, setRightSidebar]);

    return (
      <>
        <UnifiedSidebar
          config={sidebarConfig}
          context={sidebarContext}
          open={rightSidebar.isOpen}
          onOpenChange={(open) => setRightSidebar({ isOpen: open })}
          selectedTab={rightSidebar.selectedItem}
          onTabChange={(tab) =>
            setRightSidebar({
              selectedItem: tab,
              // Reset artifact-specific state when switching tabs
              isArtifactItemOpen: false,
              currentArtifactId: null,
            })
          }
          width={rightSidebar.width}
          onWidthChange={(width) => setRightSidebar({ width })}
        />

        {isMobile && (
          <SidebarToggleButton
            open={showSideBar}
            onClick={handleMobileToggle}
            badgeContent={totalSidebarItems}
            tooltipTitle={
              showSideBar ? "Close sidebar" : `View ${totalSidebarItems} items`
            }
            show={totalSidebarItems > 0}
          />
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.responseSources === nextProps.responseSources &&
      prevProps.lastAssistantMessage?.id ===
        nextProps.lastAssistantMessage?.id &&
      prevProps.onIdentifyStages === nextProps.onIdentifyStages &&
      prevProps.isIdentifyingStages === nextProps.isIdentifyingStages
    );
  },
);

ProposalGenerationSidebar.displayName = "ProposalGenerationSidebar";

export default ProposalGenerationSidebar;
